/* global COUNTRY_CODES: true */
/* eslint-disable no-param-reassign */
import {
    Object3D,
    LineBasicMaterial,
    MeshBasicMaterial,
    ShapeGeometry,
    BackSide,
    TextureLoader,
    Shape,
    Mesh,
    Vector3,
    Vector2,
    Color,
    Line,
    PlaneGeometry,
    RepeatWrapping,
} from 'three';
import { feature as topoFeature } from 'topojson-client';
import { presimplify, simplify } from 'topojson-simplify';
import { geoMercator, geoPath } from 'd3-geo';
import { TweenMax } from 'gsap';
import 'whatwg-fetch';

import mapData from '../data/world.json';
import Triangulation from '../vendor/Triangulation';
import ThreeDrawContext from './ThreeDrawContext';
import isSmallScreen from './isSmallScreen';

Triangulation.setLibrary(Triangulation.libraries.earcut);

class Map extends Object3D {
    constructor(opts) {
        super();

        this.createLand = this.createLand.bind(this);

        this.options = {
            camera: null,
            scene: null,
            width: 100,
            height: 100,
            projectionScale: 2,
            mapUrl: 'https://unpkg.com/world-atlas@1/world/110m.json',
            mapData,
            // texture: '/img/texture_map.png',
            // textureCountry: '/img/texture_map_highlight.png',
            texture: null,
            textureCountry: null,
            // color: '#151c2b',
            color: '#29303f',
            countryColor: '#8491ae',
            // simplificationFactor: isSmallScreen() ? 0.8 : 0.5,
            simplificationFactor: 3,
            strokeColor: '#FFFFFF',
            strokeWidth: 0,
            highlightSpeed: 1,
            noiseTextureSize: 16,
            alpha: 1,
            ...opts,
        };

        this.flipObject = null;

        this.scene = this.options.scene;
        this.camera = this.options.camera;
        this.width = this.options.width;
        this.height = this.options.height;
        this.globalAlpha = this.options.alpha;
        this.texture = this.options.texture !== null ?
            new TextureLoader().load(this.options.texture) : null;
        this.textureCountry = this.options.textureCountry !== null ?
            new TextureLoader().load(this.options.textureCountry) : null;

        this.drawContext = new ThreeDrawContext();
        this.flipObject = new Object3D();
        this.projection = geoMercator();
        this.geoPath = geoPath(this.projection, this.drawContext);

        this.mapData = this.options.mapData;
        this.worldData = null;
        this.landData = null;
        this.countriesData = null;
        this.countries = null;
        this.lands = null;
        this.landGeometries = null;
        this.countriesGeometries = null;
        this.noiseMesh = null;
        this.noiseGeometry = null;
        this.noiseTexture = null;
        this.noiseMaterial = null;
    }

    build() {
        const { noiseTextureSize } = this.options;
        this.flipObject.scale.set(1, -1, 1);
        this.add(this.flipObject);

        if (this.mapData !== null) {
            this.updateMapData(this.mapData);
        } else {
            this.fetchMapData();
        }

        this.noiseTexture = new TextureLoader().load('/img/texture_map.png');
        this.noiseTexture.wrapS = RepeatWrapping;
        this.noiseTexture.wrapT = RepeatWrapping;
        this.noiseMaterial = new MeshBasicMaterial({
            map: this.noiseTexture,
            color: new Color('#00091b'),
            transparent: true,
            depthWrite: false,
        });
        const size = this.getVisibleSize();
        this.noiseGeometry = new PlaneGeometry(size.width, size.height);
        this.noiseMesh = new Mesh(this.noiseGeometry, this.noiseMaterial);
        this.noiseMesh.position.set(0, 0, 2);
        // this.noiseMesh.visible = false;
        this.noiseTexture.repeat.set(size.width / noiseTextureSize, size.height / noiseTextureSize);

        this.add(this.noiseMesh);
    }

    fetchMapData() {
        const { mapPath } = this.options;
        fetch(mapPath, {
            method: 'GET',
        })
        .then(response => response.json())
        .then(data => this.updateMapData(data));
    }

    convertToCoordinates(point) {
        const coords = this.projection(point);
        return new Vector3(
            coords[0],
            coords[1],
            0,
        );
    }

    updateProjection() {
        if (this.mapData === null) {
            return;
        }
        const { width, height } = this.getVisibleSize();
        this.projection = this.projection
            .fitSize([width, height], this.countriesData)
            .rotate([-11.5, 0])
            .translate([0, 30]);
    }

    updateMapData(data) {
        const { simplificationFactor } = this.options;
        this.worldData = simplify(presimplify(data), simplificationFactor);
        const landsObjects = this.worldData.objects.land;
        const countriesObjects = this.worldData.objects.countries;
        const countries = {
            ...countriesObjects,
            geometries: countriesObjects.geometries.filter(country => (
                country.id !== COUNTRY_CODES.ATA
            )),
        };
        const lands = {
            ...landsObjects,
            geometries: landsObjects.geometries.map(geometry => ({
                ...geometry,
                arcs: geometry.arcs.filter((arc, index) => (
                    index !== 10
                )),
            })),
        };
        this.landData = topoFeature(this.worldData, lands);
        this.countriesData = topoFeature(this.worldData, countries);
        this.countriesCodes = this.countriesData.features.reduce((polygons, feature) => {
            const countryCode = Object.keys(COUNTRY_CODES).find(code => (
                COUNTRY_CODES[code] === feature.id
            )) || feature.id;
            feature.geometry.coordinates.forEach((coords) => {
                if (feature.geometry.type === 'MultiPolygon') {
                    coords.forEach(() => {
                        polygons.push(countryCode);
                    });
                } else {
                    polygons.push(countryCode);
                }
            });
            return polygons;
        }, []);

        this.updateProjection();

        this.updateGeometries();

        if (this.lands === null) {
            this.createLands();
        } else {
            this.updateLands();
        }

        if (this.countries === null) {
            this.createCountries();
        } else {
            this.updateCountries();
        }
    }

    updateGeometries() {
        this.drawContext.reset();
        this.geoPath(this.countriesData);
        this.countriesGeometries = this.drawContext.getGeometries();

        this.drawContext.reset();
        this.geoPath(this.landData);
        this.landGeometries = this.drawContext.getGeometries();
    }


    createCountries() {
        const { countryColor } = this.options;
        this.countries = this.countriesGeometries.map(geometry => (
            this.createLand(geometry, countryColor)
        ));
        this.countries.forEach((obj) => {
            obj.visible = false;
            obj.shape.material.opacity = 0;
            obj.wantedOpacity = 0;
            obj.currentOpacity = 0;
            this.flipObject.add(obj);
        });
    }

    createLands() {
        const { color } = this.options;
        this.lands = this.landGeometries.map(geometry => (
            this.createLand(geometry, color)
        ));
        this.lands.forEach((obj) => {
            obj.visible = true;
            this.flipObject.add(obj);
        });
    }

    // eslint-disable-next-line class-methods-use-this
    createLandShape(geometry) {
        const points = geometry.vertices.map(vertice => new Vector2(vertice.x, vertice.y));
        const shape = new Shape(points);
        return shape;
    }

    createShapeGeometry(geometry) {
        const shape = this.createLandShape(geometry);
        const shapeGeometry = new ShapeGeometry(shape);
        shapeGeometry.shape = shape;
        return shapeGeometry;
    }

    createLand(geometry, color) {
        const {
            strokeColor,
            strokeWidth,
        } = this.options;

        const country = new Object3D();

        // Shape
        const shapeGeometry = this.createShapeGeometry(geometry);
        const shapeMaterial = new MeshBasicMaterial({
            color: color !== null ? new Color(color) : null,
            side: BackSide,
            transparent: true,
            depthWrite: false,
            opacity: this.globalAlpha,
        });
        const shape = new Mesh(shapeGeometry, shapeMaterial);
        country.shape = shape;
        country.add(shape);

        // Line
        if (strokeWidth > 0) {
            const lineGeometry = shapeGeometry.shape.createPointsGeometry();
            const lineMaterial = new LineBasicMaterial({
                color: new Color(strokeColor),
                transparent: true,
            });
            const line = new Line(lineGeometry, lineMaterial);
            country.line = line;
            country.add(line);
        }

        return country;
    }

    updateCountries() {
        if (this.countries === null) {
            return;
        }

        const { countryColor } = this.options;
        this.countriesGeometries.forEach((geometry, index) => {
            if (typeof this.countries[index] === 'undefined') {
                this.countries[index] = this.createLand(geometry, countryColor);
                this.countries[index].visible = false;
                this.countries[index].wantedOpacity = 0;
                this.countries[index].currentOpacity = 0;
                this.countries[index].shape.material.opacity = 0;
                this.flipObject.add(this.countries[index]);
            }
            const country = this.countries[index];
            this.updateObject(country, geometry);
        });
    }

    updateLands() {
        if (this.lands === null) {
            return;
        }

        const { color } = this.options;
        this.landGeometries.forEach((geometry, index) => {
            if (typeof this.lands[index] === 'undefined') {
                this.lands[index] = this.createLand(geometry, color);
                this.lands[index].visible = true;
                this.flipObject.add(this.lands[index]);
            }
            const land = this.lands[index];
            this.updateObject(land, geometry);
        });
    }

    updateObject(object, geometry) {
        const shapeGeometry = this.createShapeGeometry(geometry);
        object.shape.geometry = shapeGeometry;
        object.shape.updateMatrix();
        object.shape.geometry.applyMatrix(object.shape.matrix);
        object.shape.matrix.identity();

        if (typeof object.line !== 'undefined') {
            const lineGeometry = shapeGeometry.shape.createPointsGeometry();
            object.line.geometry = lineGeometry;
            object.line.updateMatrix();
        }

        object.updateMatrix();
    }

    getVisibleSize() {
        const camera = this.camera;
        const mapDistance = camera.position.distanceTo(this.position);
        const vFOV = camera.fov * (Math.PI / 180);
        const aspect = this.width / this.height;
        const visibleHeight = 2 * Math.tan(vFOV / 2) * mapDistance;
        const visibleWidth = visibleHeight * aspect;
        return {
            width: visibleWidth,
            height: visibleHeight,
        };
    }

    setSize(width, height) {
        this.width = width;
        this.height = height;

        this.updateProjection();

        this.updateGeometries();

        this.updateLands();

        this.updateCountries();

        const { noiseTextureSize } = this.options;
        const size = this.getVisibleSize();
        this.noiseGeometry = new PlaneGeometry(size.width, size.height);
        this.noiseMesh.geometry = this.noiseGeometry;
        this.noiseMesh.updateMatrix();
        this.noiseTexture.repeat.set(size.width / noiseTextureSize, size.height / noiseTextureSize);
        this.noiseMaterial.needsUpdate = true;
        this.noiseTexture.needsUpdate = true;
    }


    setVisibleCountries(codes) {
        const indexesOpacity = this.countriesCodes.map(code => codes[code] || 0);
        this.countries.forEach((country, index) => {
            const opacity = indexesOpacity[index];
            if (opacity === 0 && !country.visible) {
                return;
            }
            this.tweenOpacity(country, opacity);
        });
    }

    tweenOpacity(obj, opacity) {
        const { highlightSpeed } = this.options;
        const material = obj.shape.material;
        const speed = highlightSpeed * (opacity === 0 ? 2 : (1 + Math.random()));
        TweenMax.killTweensOf(obj);
        obj.wantedOpacity = opacity;
        TweenMax.to(obj, speed, {
            currentOpacity: opacity,
            onStart: () => {
                if (!obj.visible && opacity > 0) {
                    obj.visible = true;
                }
            },
            onUpdate: () => {
                material.opacity = obj.currentOpacity * this.globalAlpha;
            },
            onComplete: () => {
                if (material.opacity === 0) {
                    obj.visible = false;
                } else {
                    TweenMax.to(obj, 2, {
                        repeat: -1,
                        yoyo: true,
                        currentOpacity: opacity * 0.6,
                        onUpdate: () => {
                            material.opacity = obj.currentOpacity * this.globalAlpha;
                        },
                    });
                }
            },
        });
    }

    set alpha(alpha) {
        this.globalAlpha = alpha;
        if (alpha <= 0) {
            this.visible = false;
        } else if (!this.visible) {
            this.visible = true;
        }

        if (this.countries == null || this.lands === null) {
            return;
        }

        this.noiseMesh.material.opacity = alpha;

        const { strokeWidth } = this.options;
        this.countries.forEach((obj) => {
            if (!obj.visible) {
                return;
            }
            TweenMax.killTweensOf(obj);
            obj.shape.material.opacity = this.globalAlpha * obj.currentOpacity;
            if (strokeWidth > 0) {
                obj.line.material.opacity = this.globalAlpha * obj.currentOpacity;
            }
        });
        this.lands.forEach((obj) => {
            obj.shape.material.opacity = this.globalAlpha;
            if (strokeWidth > 0) {
                obj.line.material.opacity = this.globalAlpha;
            }
        });
    }

    get alpha() {
        return this.globalAlpha;
    }

    set mapScale(scale) {
        this.flipObject.scale.set(scale, -1 * scale, scale);
    }

    get mapScale() {
        return this.flipObject.scale.x;
    }
}

export default Map;



// WEBPACK FOOTER //
// ./lib/Map.js